import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { tokenObject } from 'model/tokenObj';
import { LoginResponse, SessionUser } from 'model/userSession';
import localStorageService from './localStorageService';

export function resolveApiBaseUrl(): string {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const protocol = window.location.protocol || 'http:';
    return `${protocol}//${window.location.hostname}:8080`;
  }
  return 'http://127.0.0.1:8080';
}

export const API_BASE_URL = resolveApiBaseUrl();

export interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

export interface DsEntry {
  name: string;
  path: string;
  directory: boolean;
  size: number;
  lastModifiedEpochMs?: number;
  createdEpochMs?: number;
  owner: string;
  group: string;
  tags?: string[];
}

export interface ThumbnailItem {
  path: string;
  contentType?: string;
  dataBase64?: string;
  skipped?: boolean;
}

export interface MediaTicket {
  ticket: string;
  path: string;
  expiresInMs: number;
}

export interface MusicScanResult {
  paths: string[];
  truncated: boolean;
}

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean; _skipAuthRetry?: boolean };

/** Shared axios instance for auth + mini apps. */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

export function authHeaders(): AxiosRequestConfig {
  return {
    headers: {
      Authorization: `Bearer ${localStorageService.getSessionToken().getFullString()}`,
      'Content-Type': 'application/json',
    },
  };
}

export const AUTH_REQUIRED_EVENT = 'argon-auth-required';

function sendToGate(): void {
  localStorageService.clearTokens();
  if (typeof window === 'undefined') {
    return;
  }
  const path = window.location.pathname || '';
  if (path.startsWith('/gate')) {
    return;
  }
  window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT));
}

function tokenExpired(token: string): boolean {
  if (!token) {
    return true;
  }
  try {
    return new tokenObject(token).getRemainingMs() <= 0;
  } catch {
    return true;
  }
}

let renewLock: Promise<string> | null = null;

export function renewSessionToken(): Promise<string> {
  if (!renewLock) {
    renewLock = (async () => {
      const token = localStorageService.getSessionToken().getFullString();
      if (!token) {
        throw new Error('No session token');
      }
      const res = await apiClient.post<ApiEnvelope<LoginResponse>>(
        '/auth/renew',
        {},
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          _skipAuthRetry: true,
        } as RetryConfig
      );
      const data = res.data?.data;
      const next = data?.sessionToken;
      if (!res.data?.success || !next) {
        throw new Error(res.data?.message || 'Renew failed');
      }
      localStorageService.saveSessionToken(next);
      const user: SessionUser = {
        username: data.username,
        homePath: data.homePath,
        smbHostRef: data.smbHostRef,
        userProfile: data.userProfile || null,
        profileLoaded: !!data.userProfile,
      };
      localStorageService.saveSessionUser(user);
      return next;
    })().finally(() => {
      renewLock = null;
    });
  }
  return renewLock;
}

apiClient.interceptors.response.use(
  (ok) => ok,
  async (err: AxiosError) => {
    const cfg = err.config as RetryConfig | undefined;
    if (!cfg || err.response?.status !== 401 || cfg._retry || cfg._skipAuthRetry) {
      return Promise.reject(err);
    }
    const token = localStorageService.getSessionToken().getFullString();
    if (!token || tokenExpired(token)) {
      sendToGate();
      return Promise.reject(err);
    }
    cfg._retry = true;
    try {
      const next = await renewSessionToken();
      cfg.headers = cfg.headers || {};
      cfg.headers.Authorization = `Bearer ${next}`;
      return apiClient(cfg);
    } catch (renewError) {
      const renewStatus = (renewError as AxiosError | undefined)?.response?.status;
      // Android may resume before networking is ready. Keep the local login on
      // timeout/offline/server errors; only an explicit auth rejection logs out.
      if (renewStatus === 401 || renewStatus === 403) {
        sendToGate();
      }
      return Promise.reject(err);
    }
  }
);
